import { Controller } from "@hotwired/stimulus"

// Admin Dashboard Controller
// Connects to data-controller="admin-dashboard"
// Uses ECharts for visualizations (loaded globally via Phoenix theme)

export default class extends Controller {
  static targets = [
    "memoryGauge", "hitRateGauge", "jobSuccessGauge", "cacheFillGauge",
    "realtimeOpsChart", "queueSizesChart", "ttlDistChart", "cacheTypeChart",
    "keysNamespaceChart", "treemapChart",
    "statRedisKeys", "statCachedItems", "statOpsSec", "statQueueLatency",
    "memoryUsedText", "cacheHitsText", "cacheMissesText",
    "jobsProcessedText", "jobsFailedText", "cachedFeedsText",
    "cacheEntriesTbody", "jobHistoryTbody", "workersTbody"
  ]

  connect() {
    this.charts = {}
    this.realtimeData = []
    this.maxRealtimePoints = 60
    this.cacheEntries = []
    this.currentFilter = 'all'

    // Wait for ECharts to be available
    if (typeof echarts === 'undefined') {
      console.error('ECharts not loaded')
      // Retry after a short delay
      setTimeout(() => this.connect(), 500)
      return
    }

    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      this.initCharts()
      this.refreshAllData()
    })

    // Auto-refresh every 10 seconds
    this.refreshInterval = setInterval(() => this.refreshAllData(), 10000)

    // Setup filter badges
    this.element.querySelectorAll('.filter-badge').forEach(badge => {
      badge.addEventListener('click', (e) => this.handleFilterClick(e))
    })
  }

  disconnect() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
    // Dispose charts to prevent memory leaks
    Object.values(this.charts).forEach(chart => {
      if (chart && typeof chart.dispose === 'function') {
        chart.dispose()
      }
    })
  }

  // ============ Theme Colors ============
  
  getThemeColors() {
    const isDark = document.documentElement.dataset.nfrTheme === 'dark' ||
      document.documentElement.classList.contains('nfr-theme-dark')

    return {
      primary: '#2f80ed',
      success: '#2f6f59',
      warning: '#a15c00',
      danger: '#c0343d',
      info: '#057f95',
      textPrimary: isDark ? '#e3e6ed' : '#141824',
      textSecondary: isDark ? '#9fa6bc' : '#5b6578',
      border: isDark ? '#373e53' : 'rgba(15, 23, 42, 0.1)',
      background: isDark ? '#141824' : '#ffffff',
      cardBg: isDark ? '#1e2435' : '#ffffff'
    }
  }

  // ============ Chart Initialization ============

  initCharts() {
    const colors = this.getThemeColors()

    // Initialize all gauge charts
    if (this.hasMemoryGaugeTarget) {
      this.charts.memoryGauge = echarts.init(this.memoryGaugeTarget)
      this.charts.memoryGauge.setOption(this.getGaugeOption('Memory', 0, colors.primary))
    }

    if (this.hasHitRateGaugeTarget) {
      this.charts.hitRateGauge = echarts.init(this.hitRateGaugeTarget)
      this.charts.hitRateGauge.setOption(this.getGaugeOption('Hit Rate', 0, colors.success))
    }

    if (this.hasJobSuccessGaugeTarget) {
      this.charts.jobSuccessGauge = echarts.init(this.jobSuccessGaugeTarget)
      this.charts.jobSuccessGauge.setOption(this.getGaugeOption('Success', 0, colors.info))
    }

    if (this.hasCacheFillGaugeTarget) {
      this.charts.cacheFillGauge = echarts.init(this.cacheFillGaugeTarget)
      this.charts.cacheFillGauge.setOption(this.getGaugeOption('Fill', 0, colors.warning))
    }

    // Initialize other charts
    if (this.hasRealtimeOpsChartTarget) {
      this.charts.realtimeOps = echarts.init(this.realtimeOpsChartTarget)
      this.charts.realtimeOps.setOption(this.getRealtimeLineOption(colors))
    }

    if (this.hasQueueSizesChartTarget) {
      this.charts.queueSizes = echarts.init(this.queueSizesChartTarget)
      this.charts.queueSizes.setOption(this.getBarChartOption([], [], colors))
    }

    if (this.hasTtlDistChartTarget) {
      this.charts.ttlDist = echarts.init(this.ttlDistChartTarget)
      this.charts.ttlDist.setOption(this.getPieChartOption([], colors))
    }

    if (this.hasCacheTypeChartTarget) {
      this.charts.cacheType = echarts.init(this.cacheTypeChartTarget)
      this.charts.cacheType.setOption(this.getPieChartOption([], colors))
    }

    if (this.hasKeysNamespaceChartTarget) {
      this.charts.keysNamespace = echarts.init(this.keysNamespaceChartTarget)
      this.charts.keysNamespace.setOption(this.getPieChartOption([], colors))
    }

    if (this.hasTreemapChartTarget) {
      this.charts.treemap = echarts.init(this.treemapChartTarget)
      this.charts.treemap.setOption(this.getTreemapOption(colors))
    }

    // Handle window resize
    window.addEventListener('resize', () => this.resizeCharts())
  }

  resizeCharts() {
    Object.values(this.charts).forEach(chart => {
      if (chart && typeof chart.resize === 'function') {
        chart.resize()
      }
    })
  }

  // Ensure a chart is initialized, initializing it if needed
  ensureChartInitialized(chartName, targetName) {
    if (this.charts[chartName]) return true
    
    const targetMethod = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
    const targetGetter = `${targetName}Target`
    
    if (this[targetMethod] && this[targetGetter]) {
      try {
        this.charts[chartName] = echarts.init(this[targetGetter])
        return true
      } catch (e) {
        console.error(`Failed to initialize chart ${chartName}:`, e)
        return false
      }
    }
    return false
  }

  // ============ Chart Options ============

  getGaugeOption(name, value, color) {
    const colors = this.getThemeColors()
    return {
      series: [{
        type: 'gauge',
        startAngle: 200,
        endAngle: -20,
        min: 0,
        max: 100,
        splitNumber: 5,
        itemStyle: { color: color },
        progress: { show: true, width: 20 },
        pointer: { show: false },
        axisLine: { lineStyle: { width: 20, color: [[1, '#e5e5e5']] } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        title: { fontSize: 12, color: colors.textSecondary, offsetCenter: [0, '70%'] },
        detail: {
          fontSize: 24,
          fontWeight: 'bold',
          color: colors.textPrimary,
          offsetCenter: [0, '0%'],
          formatter: '{value}%'
        },
        data: [{ value: Math.round(value), name: name }]
      }]
    }
  }

  getRealtimeLineOption(colors) {
    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: colors.cardBg,
        borderColor: colors.border,
        textStyle: { color: colors.textPrimary }
      },
      grid: { left: 50, right: 20, top: 20, bottom: 30 },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: [],
        axisLine: { lineStyle: { color: colors.border } },
        axisLabel: { color: colors.textSecondary, fontSize: 10 }
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: colors.textSecondary },
        splitLine: { lineStyle: { color: colors.border, type: 'dashed' } }
      },
      series: [{
        name: 'Ops/sec',
        type: 'line',
        smooth: true,
        symbol: 'none',
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: colors.primary + '40' },
            { offset: 1, color: colors.primary + '05' }
          ])
        },
        lineStyle: { color: colors.primary, width: 2 },
        data: []
      }]
    }
  }

  getBarChartOption(labels, values, colors) {
    // Check if there's any data
    const hasData = labels && labels.length > 0
    
    if (!hasData) {
      return {
        title: {
          text: 'No queues',
          left: 'center',
          top: 'center',
          textStyle: { color: colors.textSecondary, fontSize: 14, fontWeight: 'normal' }
        },
        xAxis: { show: false },
        yAxis: { show: false },
        series: []
      }
    }
    
    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: colors.cardBg,
        borderColor: colors.border,
        textStyle: { color: colors.textPrimary }
      },
      grid: { left: 10, right: 10, top: 10, bottom: 30, containLabel: true },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: colors.border } },
        axisLabel: { color: colors.textSecondary, fontSize: 10, rotate: 30 }
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: colors.textSecondary },
        splitLine: { lineStyle: { color: colors.border, type: 'dashed' } }
      },
      series: [{
        type: 'bar',
        data: values.map((v, i) => ({
          value: v,
          itemStyle: {
            color: [colors.primary, colors.success, colors.warning, colors.info][i % 4],
            borderRadius: [4, 4, 0, 0]
          }
        })),
        barWidth: '60%'
      }]
    }
  }

  getPieChartOption(data, colors) {
    // Check if all values are zero or empty
    const hasData = data && data.length > 0 && data.some(d => d.value > 0)
    
    if (!hasData) {
      return {
        title: {
          text: 'No data available',
          left: 'center',
          top: 'center',
          textStyle: {
            color: colors.textSecondary,
            fontSize: 14,
            fontWeight: 'normal'
          }
        },
        series: []
      }
    }
    
    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: colors.cardBg,
        borderColor: colors.border,
        textStyle: { color: colors.textPrimary },
        formatter: '{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'center',
        textStyle: { color: colors.textSecondary, fontSize: 11 }
      },
      series: [{
        type: 'pie',
        radius: ['45%', '70%'],
        center: ['35%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 4, borderColor: colors.background, borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 14, fontWeight: 'bold' } },
        data: data
      }]
    }
  }

  getTreemapOption(colors) {
    return {
      tooltip: {
        formatter: (info) => `<strong>${info.name}</strong><br/>Size: ${info.value} KB`
      },
      series: [{
        type: 'treemap',
        width: '100%',
        height: '100%',
        roam: false,
        nodeClick: 'zoomToNode',
        breadcrumb: { show: true, itemStyle: { color: colors.primary } },
        label: { show: true, formatter: '{b}', fontSize: 11 },
        itemStyle: { borderColor: colors.background, borderWidth: 2, gapWidth: 2 },
        levels: [
          { itemStyle: { borderWidth: 0, gapWidth: 5 } },
          { colorSaturation: [0.3, 0.6], itemStyle: { borderColorSaturation: 0.7, gapWidth: 2, borderWidth: 2 } }
        ],
        data: []
      }]
    }
  }

  updateGauge(chart, value) {
    if (chart && typeof chart.setOption === 'function') {
      chart.setOption({ series: [{ data: [{ value: Math.round(value) }] }] })
    }
  }

  // ============ Data Fetching ============

  async refreshAllData() {
    try {
      const [redisData, sidekiqData, cacheData] = await Promise.all([
        fetch('/admin/dashboard/redis_data', { headers: { 'Accept': 'application/json' } }).then(r => r.json()),
        fetch('/admin/dashboard/sidekiq_data', { headers: { 'Accept': 'application/json' } }).then(r => r.json()),
        fetch('/admin/dashboard/feed_cache_data', { headers: { 'Accept': 'application/json' } }).then(r => r.json())
      ])

      this.updateRedisData(redisData)
      this.updateSidekiqData(sidekiqData)
      this.updateCacheData(cacheData)

    } catch (error) {
      console.error('Error refreshing data:', error)
      if (typeof toastr !== 'undefined') {
        toastr.error('Failed to refresh dashboard data')
      }
    }
  }

  updateRedisData(data) {
    const overview = data.overview || {}
    const colors = this.getThemeColors()

    // Update stats
    if (this.hasStatRedisKeysTarget) {
      this.statRedisKeysTarget.textContent = (overview.total_keys || 0).toLocaleString()
    }
    if (this.hasStatOpsSecTarget) {
      this.statOpsSecTarget.textContent = (overview.ops_per_sec || 0).toLocaleString()
    }
    if (this.hasMemoryUsedTextTarget) {
      this.memoryUsedTextTarget.textContent = overview.used_memory_human || '-'
    }

    // Update memory gauge
    const memoryMB = (overview.used_memory_bytes || 0) / (1024 * 1024)
    const memoryPercent = Math.min((memoryMB / 512) * 100, 100)
    this.updateGauge(this.charts.memoryGauge, memoryPercent)

    // Update hit rate gauge
    this.updateGauge(this.charts.hitRateGauge, overview.hit_rate || 0)

    // Update real-time chart
    const now = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
    this.realtimeData.push({ time: now, value: overview.ops_per_sec || 0 })
    if (this.realtimeData.length > this.maxRealtimePoints) {
      this.realtimeData.shift()
    }

    if (this.charts.realtimeOps) {
      this.charts.realtimeOps.setOption({
        xAxis: { data: this.realtimeData.map(d => d.time) },
        series: [{ data: this.realtimeData.map(d => d.value) }]
      })
    }

    // Update keys namespace chart
    const keysData = data.keys || {}
    const namespaceData = Object.entries(keysData).slice(0, 6).map(([name, value], i) => ({
      name: name.length > 15 ? name.substring(0, 15) + '...' : name,
      value: value,
      itemStyle: { color: [colors.primary, colors.success, colors.warning, colors.info, colors.danger, '#9333ea'][i] }
    }))
    this.ensureChartInitialized('keysNamespace', 'keysNamespaceChart')
    if (this.charts.keysNamespace) {
      this.charts.keysNamespace.setOption(this.getPieChartOption(namespaceData, colors))
    }
  }

  updateSidekiqData(data) {
    const overview = data.overview || {}
    const colors = this.getThemeColors()

    // Update stats
    if (this.hasStatQueueLatencyTarget) {
      this.statQueueLatencyTarget.textContent = (overview.default_queue_latency || 0).toFixed(2) + 's'
    }
    if (this.hasJobsProcessedTextTarget) {
      this.jobsProcessedTextTarget.textContent = (overview.processed || 0).toLocaleString()
    }
    if (this.hasJobsFailedTextTarget) {
      this.jobsFailedTextTarget.textContent = (overview.failed || 0).toLocaleString()
    }

    // Update job success gauge
    const total = (overview.processed || 0) + (overview.failed || 0)
    const successRate = total > 0 ? ((overview.processed || 0) / total) * 100 : 100
    this.updateGauge(this.charts.jobSuccessGauge, successRate)

    // Update queue sizes chart
    const queues = data.queues || []
    if (this.charts.queueSizes) {
      this.charts.queueSizes.setOption(this.getBarChartOption(
        queues.map(q => q.name),
        queues.map(q => q.size),
        colors
      ))
    }

    // Update job history table
    if (this.hasJobHistoryTbodyTarget) {
      this.jobHistoryTbodyTarget.innerHTML = (data.history || []).slice(0, 10).map(job => `
        <tr>
          <td>
            <span class="nfr-admin-strong">${(job.type || 'Job').replace('Worker', '')}</span>
            ${job.task ? `<br><small class="nfr-muted-text">${job.task}</small>` : ''}
          </td>
          <td><span class="status-dot ${job.status || 'completed'}"></span>${job.status || 'unknown'}</td>
          <td class="nfr-table-cell-end">${job.duration ? job.duration.toFixed(1) + 's' : '-'}</td>
        </tr>
      `).join('') || '<tr><td colspan="3" class="nfr-admin-table-empty">No job history</td></tr>'
    }

    // Update workers table
    if (this.hasWorkersTbodyTarget) {
      this.workersTbodyTarget.innerHTML = (data.workers || []).map(w => `
        <tr>
          <td><code>${w.class || 'Unknown'}</code></td>
          <td>${w.queue || 'default'}</td>
          <td class="nfr-table-cell-end">${w.running_for ? w.running_for.toFixed(1) + 's' : '-'}</td>
        </tr>
      `).join('') || '<tr><td colspan="3" class="nfr-admin-table-empty">No active workers</td></tr>'
    }
  }

  updateCacheData(data) {
    const colors = this.getThemeColors()
    const entries = data.entries || []
    this.cacheEntries = entries

    // Update stats
    const totalItems = entries.reduce((sum, e) => sum + (e.item_count || 0), 0)
    if (this.hasStatCachedItemsTarget) {
      this.statCachedItemsTarget.textContent = totalItems.toLocaleString()
    }
    if (this.hasCachedFeedsTextTarget) {
      this.cachedFeedsTextTarget.textContent = entries.length
    }

    // Update cache fill gauge
    const fillPercent = Math.min((entries.length / 100) * 100, 100)
    this.updateGauge(this.charts.cacheFillGauge, fillPercent)

    // Update TTL distribution chart
    const ttlDist = data.ttl_distribution || {}
    const ttlData = Object.entries(ttlDist).map(([name, value], i) => ({
      name: name,
      value: value,
      itemStyle: { color: [colors.danger, colors.warning, colors.info, colors.success][i] }
    }))
    this.ensureChartInitialized('ttlDist', 'ttlDistChart')
    if (this.charts.ttlDist) {
      this.charts.ttlDist.setOption(this.getPieChartOption(ttlData, colors))
    }

    // Update cache type chart
    const typeBreakdown = data.sources_vs_custom || {}
    const typeData = [
      { name: 'Sources', value: typeBreakdown.source || 0, itemStyle: { color: colors.primary } },
      { name: 'Custom', value: typeBreakdown.custom || 0, itemStyle: { color: '#6c757d' } }
    ]
    this.ensureChartInitialized('cacheType', 'cacheTypeChart')
    if (this.charts.cacheType) {
      this.charts.cacheType.setOption(this.getPieChartOption(typeData, colors))
    }

    // Update treemap
    const treemapData = entries.map(e => ({
      name: e.name || 'Unknown',
      value: e.size_kb || 0,
      itemStyle: { color: e.type === 'source' ? colors.primary : colors.info }
    })).filter(d => d.value > 0)

    // Use D3.js for treemap visualization
    if (this.hasTreemapChartTarget && typeof d3 !== 'undefined') {
      this.renderD3Treemap(treemapData, colors)
    } else if (this.hasTreemapChartTarget) {
      console.error('D3.js not loaded for treemap')
      this.treemapChartTarget.innerHTML = '<div class="nfr-admin-chart-empty">D3.js required for treemap</div>'
    }

    // Update cache entries table
    this.renderCacheEntriesTable()
  }

  renderCacheEntriesTable() {
    if (!this.hasCacheEntriesTbodyTarget) return

    const entries = this.cacheEntries.filter(e => {
      if (this.currentFilter === 'all') return true
      return e.type === this.currentFilter
    })

    this.cacheEntriesTbodyTarget.innerHTML = entries.slice(0, 50).map(entry => `
      <tr class="nfr-admin-cache-entry">
        <td data-label="Feed">
          <span class="nfr-admin-strong">${this.escapeHtml(entry.name || 'Unknown')}</span>
          <br><small class="nfr-muted-text">${(entry.identifier || '').substring(0, 12)}...</small>
        </td>
        <td data-label="Type">
          <span class="nfr-badge ${entry.type === 'source' ? 'nfr-badge-primary' : 'nfr-badge-muted'}">${entry.type || 'unknown'}</span>
        </td>
        <td data-label="Items" class="nfr-table-cell-end">${entry.item_count || 0}</td>
        <td data-label="Size" class="nfr-table-cell-end">${entry.size_kb || 0} KB</td>
        <td data-label="TTL" class="nfr-table-cell-end">
          <span class="${(entry.ttl_minutes || 0) < 5 ? 'nfr-admin-danger-text nfr-admin-strong' : ''}">${entry.ttl_minutes || 0}m</span>
        </td>
        <td data-label="Actions"><span class="nfr-muted-text">Use guarded remediation</span></td>
      </tr>
    `).join('') || '<tr><td colspan="6" class="nfr-admin-table-empty">No cache entries</td></tr>'
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // ============ Actions ============

  handleFilterClick(event) {
    const badge = event.currentTarget
    this.element.querySelectorAll('.filter-badge').forEach(b => b.classList.remove('active'))
    badge.classList.add('active')
    this.currentFilter = badge.dataset.filter
    this.renderCacheEntriesTable()
  }

  showPanelTab(event) {
    const selectedPanelId = event.params.panel
    const tabs = event.currentTarget.closest('.nfr-admin-tabs')
    if (!tabs || !selectedPanelId) return

    tabs.querySelectorAll('.nfr-admin-tab-button').forEach(button => {
      button.setAttribute('aria-selected', String(button === event.currentTarget))
    })

    tabs.querySelectorAll('.nfr-admin-tab-section').forEach(panel => {
      panel.hidden = panel.id !== selectedPanelId
    })
  }

  refresh() {
    this.refreshAllData()
  }

  // ============ D3 Treemap ============

  renderD3Treemap(data, colors) {
    const container = this.treemapChartTarget
    const width = container.clientWidth
    const height = container.clientHeight || 300

    // Clear previous content
    container.innerHTML = ''

    if (!data || data.length === 0) {
      container.innerHTML = `
        <div class="nfr-admin-chart-empty">
          <span>No cached feeds</span>
          <small>Use "Warm Cache" to populate</small>
        </div>
      `
      return
    }

    // Create hierarchical data structure for D3
    const hierarchyData = {
      name: 'Feed Cache',
      children: data.map(d => ({
        name: d.name,
        value: d.value,
        type: d.itemStyle?.color === colors.primary ? 'source' : 'custom'
      }))
    }

    // Create SVG
    const svg = d3.select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('font-family', 'inherit')

    // Create tooltip
    const tooltip = d3.select(container)
      .append('div')
      .attr('class', 'treemap-tooltip')

    // Create treemap layout
    const root = d3.hierarchy(hierarchyData)
      .sum(d => d.value)
      .sort((a, b) => b.value - a.value)

    d3.treemap()
      .size([width, height])
      .paddingOuter(3)
      .paddingInner(2)
      .round(true)(root)

    // Color scale
    const colorScale = d3.scaleOrdinal()
      .domain(['source', 'custom'])
      .range([colors.primary, colors.info])

    // Draw cells
    const cells = svg.selectAll('g')
      .data(root.leaves())
      .join('g')
      .attr('transform', d => `translate(${d.x0},${d.y0})`)

    cells.append('rect')
      .attr('width', d => Math.max(0, d.x1 - d.x0))
      .attr('height', d => Math.max(0, d.y1 - d.y0))
      .attr('fill', d => colorScale(d.data.type))
      .attr('stroke', '#fff')
      .attr('stroke-width', 1)
      .attr('rx', 3)
      .attr('ry', 3)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this).attr('opacity', 0.8)
        tooltip
          .style('visibility', 'visible')
          .html(`<strong>${d.data.name}</strong><br/>Size: ${d.data.value.toFixed(2)} KB`)
      })
      .on('mousemove', function(event) {
        tooltip
          .style('top', (event.offsetY - 10) + 'px')
          .style('left', (event.offsetX + 10) + 'px')
      })
      .on('mouseout', function() {
        d3.select(this).attr('opacity', 1)
        tooltip.style('visibility', 'hidden')
      })

    // Add labels for cells large enough
    cells.append('text')
      .attr('x', 4)
      .attr('y', 14)
      .text(d => {
        const cellWidth = d.x1 - d.x0
        const cellHeight = d.y1 - d.y0
        if (cellWidth > 60 && cellHeight > 20) {
          const name = d.data.name
          const maxChars = Math.floor(cellWidth / 7)
          return name.length > maxChars ? name.substring(0, maxChars - 2) + '...' : name
        }
        return ''
      })
      .attr('fill', '#fff')
      .attr('font-size', '11px')
      .attr('font-weight', '500')
      .style('pointer-events', 'none')

    // Add size labels for larger cells
    cells.append('text')
      .attr('x', 4)
      .attr('y', 28)
      .text(d => {
        const cellWidth = d.x1 - d.x0
        const cellHeight = d.y1 - d.y0
        if (cellWidth > 50 && cellHeight > 35) {
          return `${d.data.value.toFixed(1)} KB`
        }
        return ''
      })
      .attr('fill', 'rgba(255,255,255,0.7)')
      .attr('font-size', '10px')
      .style('pointer-events', 'none')
  }
}
